import React, { useState, useEffect } from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Grid,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip,
    Alert,
    CircularProgress,
    Divider,
    Button
} from '@mui/material';
import {
    AccountBalance as TreasuryIcon,
    Euro as EuroIcon,
    Warning as WarningIcon,
    CheckCircle as CheckCircleIcon,
    TrendingUp as TrendingUpIcon,
    ArrowBack as ArrowBackIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { treasuryService } from '../../services/api';

interface TreasuryData {
    total_tfiats: number;
    total_guide_stars: number;
    company_liability: number;
    realized_profit_total: number;
    unrealized_profit_total: number;
    deductive_balance_total: number;
    company_pending_total: number;
    system_balance: number;
    balance_status: 'balanced' | 'surplus' | 'deficit';
    balance_difference: number;
    euro_value: number;
    company_tfiats: number;
    surplus_analysis: {
        is_normal: boolean;
        explanation: string;
        breakdown: {
            guide_stars_liability: number;
            qualified_stars_distribution: number;
            surplus_amount: number;
            surplus_percentage: number;
        };
        details: {
            guide_stars_count: number;
            qualified_stars_count: number;
            reincorporated_stars_count: number;
            book_unlocks_count: number;
            company_tfiats: number;
        };
        recommendations: string[];
    };
}

const Treasury: React.FC = () => {
    const navigate = useNavigate();
    const [treasuryData, setTreasuryData] = useState<TreasuryData | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [showAnalysis, setShowAnalysis] = useState(false);

    useEffect(() => {
        loadTreasuryData();
    }, []);

    const loadTreasuryData = async () => {
        try {
            setLoading(true);
            setError(null);
            const data = await treasuryService.getTreasuryData();
            setTreasuryData(data);
        } catch (err) {
            console.error('Error loading treasury data:', err);
            setError('Error al cargar datos de tesorería');
        } finally {
            setLoading(false);
        }
    };

    const getBalanceStatusColor = (status: string) => {
        switch (status) {
            case 'balanced': return 'success';
            case 'surplus': return 'info';
            case 'deficit': return 'error';
            default: return 'default';
        }
    };

    const getBalanceStatusIcon = (status: string) => {
        switch (status) {
            case 'balanced': return <CheckCircleIcon />;
            case 'surplus': return <TrendingUpIcon />;
            case 'deficit': return <WarningIcon />;
            default: return null;
        }
    };

    const getBalanceStatusText = (status: string) => {
        switch (status) {
            case 'balanced': return 'Sistema Balanceado';
            case 'surplus': return 'Excedente del Sistema';
            case 'deficit': return 'Déficit del Sistema';
            default: return 'Estado Desconocido';
        }
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Alert severity="error" sx={{ mb: 2 }}>
                {error}
            </Alert>
        );
    }

    if (!treasuryData) {
        return (
            <Alert severity="warning" sx={{ mb: 2 }}>
                No se encontraron datos de tesorería
            </Alert>
        );
    }

    return (
        <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                <Typography variant="h4" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <TreasuryIcon color="primary" />
                    Tesorería del Sistema
                </Typography>
                <Box sx={{ display: 'flex', gap: 2 }}>
                    <Button
                        variant="contained"
                        startIcon={<TrendingUpIcon />}
                        onClick={() => setShowAnalysis(!showAnalysis)}
                        sx={{ 
                            backgroundColor: '#7C3AED',
                            '&:hover': {
                                backgroundColor: '#6D28D9'
                            }
                        }}
                    >
                        {showAnalysis ? 'Ocultar Análisis' : 'Análisis Detallado'}
                    </Button>
                    <Button
                        variant="outlined"
                        startIcon={<ArrowBackIcon />}
                        onClick={() => navigate('/admin/dashboard')}
                        sx={{ 
                            borderColor: '#7C3AED',
                            color: '#7C3AED',
                            '&:hover': {
                                borderColor: '#6D28D9',
                                backgroundColor: '#F3F4F6'
                            }
                        }}
                    >
                        Atrás
                    </Button>
                </Box>
            </Box>

            {/* Resumen General */}
            <Grid container spacing={3} sx={{ mb: 3 }}>
                <Grid item xs={12} md={3}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" color="primary" gutterBottom>
                                Total Tfiats
                            </Typography>
                            <Typography variant="h4" component="div">
                                {treasuryData.total_tfiats.toFixed(2)}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                En todo el sistema
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} md={3}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" color="primary" gutterBottom>
                                Estrellas Guía
                            </Typography>
                            <Typography variant="h4" component="div">
                                {treasuryData.total_guide_stars}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Compañía debe: {treasuryData.company_liability.toFixed(2)} TF
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} md={3}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" color="primary" gutterBottom>
                                Valor en Euros
                            </Typography>
                            <Typography variant="h4" component="div" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <EuroIcon />
                                {treasuryData.euro_value.toFixed(2)}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Ganancias Realizadas
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} md={3}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" color="success.main" gutterBottom>
                                Tfiats de la Empresa
                            </Typography>
                            <Typography variant="h4" component="div" sx={{ color: 'success.main' }}>
                                {treasuryData.company_tfiats.toFixed(2)} TF
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Ganancias reales del sistema
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} md={3}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" color="primary" gutterBottom>
                                Estado del Sistema
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                {getBalanceStatusIcon(treasuryData.balance_status)}
                                <Typography variant="h6">
                                    {getBalanceStatusText(treasuryData.balance_status)}
                                </Typography>
                            </Box>
                            <Chip
                                label={`${treasuryData.balance_difference > 0 ? '+' : ''}${treasuryData.balance_difference.toFixed(2)} TF`}
                                color={getBalanceStatusColor(treasuryData.balance_status)}
                                size="small"
                            />
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Detalles de Balances */}
            <Grid container spacing={3}>
                <Grid item xs={12} md={8}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <TreasuryIcon color="primary" />
                                Distribución Detallada de Tfiats
                            </Typography>
                            <TableContainer component={Paper} variant="outlined">
                                <Table>
                                    <TableHead>
                                        <TableRow sx={{ backgroundColor: '#F8FAFC' }}>
                                            <TableCell><strong>Tipo de Balance</strong></TableCell>
                                            <TableCell align="right"><strong>Cantidad (TF)</strong></TableCell>
                                            <TableCell align="right"><strong>Porcentaje</strong></TableCell>
                                            <TableCell align="center"><strong>Estado</strong></TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        <TableRow>
                                            <TableCell>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <Box sx={{ width: 12, height: 12, backgroundColor: '#8B5CF6', borderRadius: '50%' }} />
                                                    Balance Deductivo
                                                </Box>
                                            </TableCell>
                                            <TableCell align="right">
                                                <Typography variant="h6" color="primary">
                                                    {treasuryData.deductive_balance_total.toFixed(2)}
                                                </Typography>
                                            </TableCell>
                                            <TableCell align="right">
                                                {((treasuryData.deductive_balance_total / treasuryData.total_tfiats) * 100).toFixed(1)}%
                                            </TableCell>
                                            <TableCell align="center">
                                                <Chip label="Activo" color="primary" size="small" />
                                            </TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <Box sx={{ width: 12, height: 12, backgroundColor: '#F59E0B', borderRadius: '50%' }} />
                                                    Ganancias No Realizadas
                                                </Box>
                                            </TableCell>
                                            <TableCell align="right">
                                                <Typography variant="h6" color="warning.main">
                                                    {treasuryData.unrealized_profit_total.toFixed(2)}
                                                </Typography>
                                            </TableCell>
                                            <TableCell align="right">
                                                {((treasuryData.unrealized_profit_total / treasuryData.total_tfiats) * 100).toFixed(1)}%
                                            </TableCell>
                                            <TableCell align="center">
                                                <Chip label="Pendiente" color="warning" size="small" />
                                            </TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <Box sx={{ width: 12, height: 12, backgroundColor: '#10B981', borderRadius: '50%' }} />
                                                    Ganancias Realizadas
                                                </Box>
                                            </TableCell>
                                            <TableCell align="right">
                                                <Typography variant="h6" color="success.main">
                                                    {treasuryData.realized_profit_total.toFixed(2)}
                                                </Typography>
                                            </TableCell>
                                            <TableCell align="right">
                                                {((treasuryData.realized_profit_total / treasuryData.total_tfiats) * 100).toFixed(1)}%
                                            </TableCell>
                                            <TableCell align="center">
                                                <Chip label="Disponible" color="success" size="small" />
                                            </TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <Box sx={{ width: 12, height: 12, backgroundColor: '#EF4444', borderRadius: '50%' }} />
                                                    Cuentas por Cobrar (Compañía)
                                                </Box>
                                            </TableCell>
                                            <TableCell align="right">
                                                <Typography variant="h6" color="error.main">
                                                    {treasuryData.company_pending_total.toFixed(2)}
                                                </Typography>
                                            </TableCell>
                                            <TableCell align="right">
                                                {((treasuryData.company_pending_total / treasuryData.total_tfiats) * 100).toFixed(1)}%
                                            </TableCell>
                                            <TableCell align="center">
                                                <Chip label="Por Cobrar" color="error" size="small" />
                                            </TableCell>
                                        </TableRow>
                                        <TableRow sx={{ backgroundColor: '#F8FAFC', '& .MuiTableCell-root': { borderTop: '2px solid #E5E7EB' } }}>
                                            <TableCell><strong>TOTAL DEL SISTEMA</strong></TableCell>
                                            <TableCell align="right">
                                                <Typography variant="h5" color="primary" sx={{ fontWeight: 'bold' }}>
                                                    {treasuryData.total_tfiats.toFixed(2)}
                                                </Typography>
                                            </TableCell>
                                            <TableCell align="right">
                                                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                                                    100.0%
                                                </Typography>
                                            </TableCell>
                                            <TableCell align="center">
                                                <Chip 
                                                    label="Sistema" 
                                                    color={getBalanceStatusColor(treasuryData.balance_status)} 
                                                    size="small" 
                                                />
                                            </TableCell>
                                        </TableRow>
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} md={4}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <CheckCircleIcon color="primary" />
                                Verificación del Sistema
                            </Typography>
                            
                            <Box sx={{ mb: 3 }}>
                                <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold', color: '#374151' }}>
                                    Verificación Estrella Guía
                                </Typography>
                                <Box sx={{ 
                                    p: 2, 
                                    borderRadius: 2, 
                                    backgroundColor: treasuryData.total_guide_stars === treasuryData.company_liability ? '#F0FDF4' : '#FEF2F2',
                                    border: `1px solid ${treasuryData.total_guide_stars === treasuryData.company_liability ? '#10B981' : '#EF4444'}`
                                }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                        {treasuryData.total_guide_stars === treasuryData.company_liability ? (
                                            <CheckCircleIcon color="success" />
                                        ) : (
                                            <WarningIcon color="error" />
                                        )}
                                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                            {treasuryData.total_guide_stars === treasuryData.company_liability
                                                ? 'Sistema Correcto'
                                                : 'Error Detectado'
                                            }
                                        </Typography>
                                    </Box>
                                    <Typography variant="body2" color="text.secondary">
                                        {treasuryData.total_guide_stars} estrellas guía = {treasuryData.company_liability} TF
                                    </Typography>
                                </Box>
                            </Box>

                            <Divider sx={{ my: 2 }} />

                            <Box sx={{ mb: 3 }}>
                                <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold', color: '#374151' }}>
                                    Conversión a Euros
                                </Typography>
                                <Box sx={{ 
                                    p: 2, 
                                    borderRadius: 2, 
                                    backgroundColor: '#F0F9FF',
                                    border: '1px solid #0EA5E9'
                                }}>
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                        Tasa de conversión: 1 TF = 1 €
                                    </Typography>
                                    <Typography variant="h6" color="primary" sx={{ fontWeight: 'bold' }}>
                                        {treasuryData.realized_profit_total.toFixed(2)} TF = €{treasuryData.euro_value.toFixed(2)}
                                    </Typography>
                                </Box>
                            </Box>

                            <Divider sx={{ my: 2 }} />

                            <Box>
                                <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold', color: '#374151' }}>
                                    Estado del Balance
                                </Typography>
                                <Box sx={{ 
                                    p: 2, 
                                    borderRadius: 2, 
                                    backgroundColor: treasuryData.balance_status === 'balanced' ? '#F0FDF4' : 
                                                   treasuryData.balance_status === 'surplus' ? '#F0F9FF' : '#FEF2F2',
                                    border: `1px solid ${treasuryData.balance_status === 'balanced' ? '#10B981' : 
                                                      treasuryData.balance_status === 'surplus' ? '#0EA5E9' : '#EF4444'}`
                                }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                        {getBalanceStatusIcon(treasuryData.balance_status)}
                                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                            {getBalanceStatusText(treasuryData.balance_status)}
                                        </Typography>
                                    </Box>
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                        Diferencia: {treasuryData.balance_difference > 0 ? '+' : ''}{treasuryData.balance_difference.toFixed(2)} TF
                                    </Typography>
                                    {treasuryData.balance_status === 'balanced' && (
                                        <Typography variant="body2" color="success.main" sx={{ fontWeight: 'bold' }}>
                                            ✅ Sistema perfectamente balanceado
                                        </Typography>
                                    )}
                                    {treasuryData.balance_status === 'surplus' && (
                                        <Typography variant="body2" color="info.main" sx={{ fontWeight: 'bold' }}>
                                            ℹ️ Excedente de {treasuryData.balance_difference.toFixed(2)} TF
                                        </Typography>
                                    )}
                                    {treasuryData.balance_status === 'deficit' && (
                                        <Typography variant="body2" color="error.main" sx={{ fontWeight: 'bold' }}>
                                            ⚠️ Déficit de {Math.abs(treasuryData.balance_difference).toFixed(2)} TF
                                        </Typography>
                                    )}
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Estadísticas Adicionales */}
            <Grid container spacing={3} sx={{ mt: 2 }}>
                <Grid item xs={12} md={6}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <TrendingUpIcon color="primary" />
                                Análisis de Rendimiento
                            </Typography>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2, backgroundColor: '#F8FAFC', borderRadius: 2 }}>
                                    <Typography variant="body2" color="text.secondary">
                                        Eficiencia de Conversión
                                    </Typography>
                                    <Typography variant="h6" color="primary">
                                        {treasuryData.total_tfiats > 0 ? ((treasuryData.realized_profit_total / treasuryData.total_tfiats) * 100).toFixed(1) : 0}%
                                    </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2, backgroundColor: '#F8FAFC', borderRadius: 2 }}>
                                    <Typography variant="body2" color="text.secondary">
                                        Tfiats Pendientes
                                    </Typography>
                                    <Typography variant="h6" color="warning.main">
                                        {treasuryData.unrealized_profit_total.toFixed(2)} TF
                                    </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2, backgroundColor: '#F8FAFC', borderRadius: 2 }}>
                                    <Typography variant="body2" color="text.secondary">
                                        Promedio por Estrella Guía
                                    </Typography>
                                    <Typography variant="h6" color="info.main">
                                        {treasuryData.total_guide_stars > 0 ? (treasuryData.total_tfiats / treasuryData.total_guide_stars).toFixed(2) : 0} TF
                                    </Typography>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} md={6}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <EuroIcon color="primary" />
                                Resumen Financiero
                            </Typography>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2, backgroundColor: '#F0FDF4', borderRadius: 2, border: '1px solid #10B981' }}>
                                    <Typography variant="body2" color="text.secondary">
                                        Valor Total en Euros
                                    </Typography>
                                    <Typography variant="h5" color="success.main" sx={{ fontWeight: 'bold' }}>
                                        €{treasuryData.euro_value.toFixed(2)}
                                    </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2, backgroundColor: '#FEF2F2', borderRadius: 2, border: '1px solid #EF4444' }}>
                                    <Typography variant="body2" color="text.secondary">
                                        Deuda de la Compañía
                                    </Typography>
                                    <Typography variant="h6" color="error.main">
                                        {treasuryData.company_pending_total.toFixed(2)} TF
                                    </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2, backgroundColor: '#F0F9FF', borderRadius: 2, border: '1px solid #0EA5E9' }}>
                                    <Typography variant="body2" color="text.secondary">
                                        Balance Neto del Sistema
                                    </Typography>
                                    <Typography variant="h6" color={treasuryData.balance_difference >= 0 ? 'success.main' : 'error.main'}>
                                        {treasuryData.balance_difference > 0 ? '+' : ''}{treasuryData.balance_difference.toFixed(2)} TF
                                    </Typography>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Análisis Detallado del Excedente */}
            {showAnalysis && treasuryData && (
                <Grid container spacing={3} sx={{ mt: 2 }}>
                    <Grid item xs={12}>
                        <Card>
                            <CardContent>
                                <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                                    <TrendingUpIcon color="primary" />
                                    Análisis Detallado del Excedente
                                </Typography>
                                
                                {/* Explicación Principal */}
                                <Box sx={{ 
                                    p: 3, 
                                    borderRadius: 2, 
                                    backgroundColor: treasuryData.surplus_analysis.is_normal ? '#F0FDF4' : '#FEF2F2',
                                    border: `1px solid ${treasuryData.surplus_analysis.is_normal ? '#10B981' : '#EF4444'}`,
                                    mb: 3
                                }}>
                                    <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
                                        {treasuryData.surplus_analysis.is_normal ? '✅ Sistema Funcionando Correctamente' : '⚠️ Revisar Sistema'}
                                    </Typography>
                                    <Typography variant="body1" sx={{ mb: 2 }}>
                                        {treasuryData.surplus_analysis.explanation}
                                    </Typography>
                                </Box>

                                {/* Desglose del Excedente */}
                                <Grid container spacing={3} sx={{ mb: 3 }}>
                                    <Grid item xs={12} md={6}>
                                        <Card variant="outlined">
                                            <CardContent>
                                                <Typography variant="h6" gutterBottom>
                                                    Desglose del Excedente
                                                </Typography>
                                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2, backgroundColor: '#F8FAFC', borderRadius: 1 }}>
                                                        <Typography variant="body2" color="text.secondary">
                                                            Responsabilidad Compañía (Estrellas Guía)
                                                        </Typography>
                                                        <Typography variant="h6" color="primary">
                                                            {treasuryData.surplus_analysis.breakdown.guide_stars_liability.toFixed(2)} TF
                                                        </Typography>
                                                    </Box>
                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2, backgroundColor: '#F8FAFC', borderRadius: 1 }}>
                                                        <Typography variant="body2" color="text.secondary">
                                                            Distribución Estrellas Calificadas
                                                        </Typography>
                                                        <Typography variant="h6" color="warning.main">
                                                            {treasuryData.surplus_analysis.breakdown.qualified_stars_distribution.toFixed(2)} TF
                                                        </Typography>
                                                    </Box>
                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2, backgroundColor: '#F0F9FF', borderRadius: 1, border: '1px solid #0EA5E9' }}>
                                                        <Typography variant="body2" color="text.secondary">
                                                            <strong>Excedente Total</strong>
                                                        </Typography>
                                                        <Typography variant="h5" color="info.main" sx={{ fontWeight: 'bold' }}>
                                                            {treasuryData.surplus_analysis.breakdown.surplus_amount.toFixed(2)} TF
                                                        </Typography>
                                                    </Box>
                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2, backgroundColor: '#F8FAFC', borderRadius: 1 }}>
                                                        <Typography variant="body2" color="text.secondary">
                                                            Porcentaje del Excedente
                                                        </Typography>
                                                        <Typography variant="h6" color="text.primary">
                                                            {treasuryData.surplus_analysis.breakdown.surplus_percentage.toFixed(1)}%
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            </CardContent>
                                        </Card>
                                    </Grid>

                                    <Grid item xs={12} md={6}>
                                        <Card variant="outlined">
                                            <CardContent>
                                                <Typography variant="h6" gutterBottom>
                                                    Detalles del Sistema
                                                </Typography>
                                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2, backgroundColor: '#F8FAFC', borderRadius: 1 }}>
                                                        <Typography variant="body2" color="text.secondary">
                                                            Estrellas Guía
                                                        </Typography>
                                                        <Typography variant="h6" color="primary">
                                                            {treasuryData.surplus_analysis.details.guide_stars_count}
                                                        </Typography>
                                                    </Box>
                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2, backgroundColor: '#F8FAFC', borderRadius: 1 }}>
                                                        <Typography variant="body2" color="text.secondary">
                                                            Estrellas Calificadas
                                                        </Typography>
                                                        <Typography variant="h6" color="warning.main">
                                                            {treasuryData.surplus_analysis.details.qualified_stars_count}
                                                        </Typography>
                                                    </Box>
                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2, backgroundColor: '#F8FAFC', borderRadius: 1 }}>
                                                        <Typography variant="body2" color="text.secondary">
                                                            Estrellas Reincorporadas
                                                        </Typography>
                                                        <Typography variant="h6" color="success.main">
                                                            {treasuryData.surplus_analysis.details.reincorporated_stars_count}
                                                        </Typography>
                                                    </Box>
                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2, backgroundColor: '#F8FAFC', borderRadius: 1 }}>
                                                        <Typography variant="body2" color="text.secondary">
                                                            Libros Desbloqueados
                                                        </Typography>
                                                        <Typography variant="h6" color="info.main">
                                                            {treasuryData.surplus_analysis.details.book_unlocks_count}
                                                        </Typography>
                                                    </Box>
                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2, backgroundColor: '#F0FDF4', borderRadius: 1, border: '1px solid #10B981' }}>
                                                        <Typography variant="body2" color="text.secondary">
                                                            <strong>Tfiats de la Empresa</strong>
                                                        </Typography>
                                                        <Typography variant="h6" color="success.main" sx={{ fontWeight: 'bold' }}>
                                                            {treasuryData.surplus_analysis.details.company_tfiats.toFixed(2)} TF
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                </Grid>

                                {/* Recomendaciones */}
                                <Card variant="outlined">
                                    <CardContent>
                                        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <CheckCircleIcon color="primary" />
                                            Recomendaciones del Sistema
                                        </Typography>
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                            {treasuryData.surplus_analysis.recommendations.map((recommendation, index) => (
                                                <Box key={index} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, p: 1 }}>
                                                    <Typography variant="body2" color="primary" sx={{ fontWeight: 'bold', minWidth: '20px' }}>
                                                        {index + 1}.
                                                    </Typography>
                                                    <Typography variant="body2" color="text.primary">
                                                        {recommendation}
                                                    </Typography>
                                                </Box>
                                            ))}
                                        </Box>
                                    </CardContent>
                                </Card>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            )}
        </Box>
    );
};

export default Treasury;
